import React, { useEffect, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { useTranslation } from 'react-i18next';

// shorter on phones so each chart doesn't take a whole screen
const chartStyle = { height: typeof window !== 'undefined' && window.innerWidth < 640 ? 260 : 350, width: '100%' };

type BarChartProps = {
    title: string;
    data: number[];
    xAxis: string[];
};

const BarChart: React.FC<BarChartProps> = ({ title, data, xAxis }) => {
    const { t } = useTranslation();
    const formattedData = data.map((value: number) => {
        const isNegative = value < 0;
        return {
            value,
            itemStyle: {
                color: isNegative ? '#ef4444' : '#3b82f6',
                borderColor: isNegative ? '#dc2626' : '#2563eb',
                borderWidth: 2,
                borderRadius: [4, 4, 0, 0],
                shadowColor: isNegative ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)',
                shadowBlur: 8,
                shadowOffsetY: 4,
            },
        };
    });

    const option = {
        backgroundColor: 'transparent',
        title: {
            text: title,
            left: 'center',
            top: 20,
            textStyle: { 
                fontSize: 18,
                fontWeight: 'bold',
                color: '#1f2937',
                fontFamily: "Roboto, sans-serif",
            },
        },
        grid: {
            top: 60,
            bottom: 40,
            left: 50,
            right: 30,
            containLabel: true,
        },
        tooltip: {
            trigger: 'axis',
            axisPointer: { 
                type: 'shadow',
                shadowStyle: {
                    color: 'rgba(0, 0, 0, 0.1)',
                    shadowBlur: 10,
                }
            },
            backgroundColor: '#ffffff',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            borderRadius: 8,
            padding: [12, 16],
            textStyle: {
                color: '#374151',
                fontSize: 13,
                fontWeight: '500',
            },
            formatter: (params: any) => {
                const item = params[0];
                const day = t(item.name);
                const result = item.value;
                const isPositive = result >= 0;
                const resultFormatted = `${isPositive ? '+' : '-'}$${Math.abs(result).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const resultColor = isPositive ? '#2563eb' : '#dc2626';
                const icon = isPositive ? '📊' : '📉';

                return `
                  <div style="padding: 4px 0;">
                    <div style="font-size: 14px; font-weight: 600; color: #1f2937; margin-bottom: 8px;">
                      ${icon} ${day}
                    </div>
                    <div style="display: flex; align-items: center; gap: 8px;">
                      <div style="width: 12px; height: 12px; border-radius: 50%; background: ${item.color};"></div>
                      <span style="color: #6b7280;">${t('Profit')}:</span>
                      <span style="color: ${resultColor}; font-weight: 600; font-size: 14px;">${resultFormatted}</span>
                    </div>
                  </div>
                `;
            },
        },
        xAxis: {
            type: 'category',
            data: xAxis,
            axisLabel: { 
                interval: 0, 
                rotate: 0,
                color: '#6b7280',
                fontSize: 12,
                fontWeight: '500',
                formatter: (value: string) => {
                    const day = t(value);
                    return day;
                }
            },
            axisLine: { 
                show: true,
                lineStyle: {
                    color: '#e5e7eb',
                    width: 1,
                }
            },
            axisTick: { 
                show: true,
                lineStyle: {
                    color: '#e5e7eb',
                }
            },
            splitLine: {
                show: false,
            }
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: '#6b7280',
                fontSize: 12,
                formatter: (value: number) => `$${value.toLocaleString()}`,
            },
            axisLine: {
                show: false,
            },
            axisTick: {
                show: false,
            },
            splitLine: {
                lineStyle: {
                    color: '#f3f4f6',
                    type: 'dashed',
                }
            }
        },
        series: [
            {
                type: 'bar',
                data: formattedData,
                barWidth: '60%',
                barCategoryGap: '20%',
                barGap: '10%',
                itemStyle: {
                    color: '#3b82f6',
                    borderColor: '#2563eb',
                    borderWidth: 2,
                    borderRadius: [4, 4, 0, 0],
                },
                emphasis: {
                    itemStyle: {
                        shadowBlur: 15,
                        shadowColor: 'rgba(59, 130, 246, 0.4)',
                        borderColor: '#1d4ed8',
                        borderWidth: 3,
                    },
                },
                animationDelay: (idx: number) => idx * 100,
                animationDuration: 800,
            },
        ],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'cubicOut',
    };

    return <ReactECharts option={option} style={chartStyle} />;
};


interface DashboardChartsProp {
    dailyAnalysis: DailyAnalysis[]
    pdfShow?: boolean
}

interface BaseAnalysis {
  trades: number
  profit: number
  win: number
  losses: number
  win_rate: number
}

interface DailyAnalysis extends BaseAnalysis {
  day: string
}

const DashboardCharts = (props: DashboardChartsProp) => {
    const { t } = useTranslation();
    const { dailyAnalysis, pdfShow } = props

    return (
        <div className={`space-y-6 ${pdfShow ? 'max-w-full' : 'min-w-full'}`}>
            <div className={`portlet ${pdfShow ? 'max-w-full' : 'min-w-full'}`} style={{
                width: pdfShow ? "1075px" : '100%',
            }}>
                <BarChart
                    title={t('ProfitbyDay')}
                    xAxis={dailyAnalysis.map(item => item.day)}
                    data={dailyAnalysis.map(item => item.profit)}
                />
            </div>
        </div>
    );
};

export default DashboardCharts;
